import { MouseEvent, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { DECISION_INFO } from 'CorePackage/MainModule/routes/routes';
import {
  KNO_ORGANIZATION_BLOCK_ID,
  BASIS_DOCUMENT_DATE_TIME_BLOCK_ID,
  BASIS_DOCUMENT_NUMBER_BLOCK_ID,
  KIND_BLOCK_ID,
  KIND_CONTROL_BLOCK_ID,
} from 'DecisionPackage/MainModule/constants/decisionFieldsElementsIds';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { getDictionariesKnoOrganizationsSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Row } from 'UIPackage/FormModule/components/Row';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import {
  getDecisionDataAccessToken,
  getDecisionDataDecisionId,
  getDecisionDataSignatureInfo,
  getDecisionDataSigned,
  getDecisionDecisionKindsSelectOptions,
  getDecisionFormRef,
  getDecisionIsNew,
  getDecisionMassCancel,
  getDecisionMassDelete,
  getDecisionSupervisionTypesSelectOptions,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionaryDecisionKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDecisionKindSelectOption';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { IKnmSignatureInfo } from 'APIPackage/KnmModule/interfaces/IKnmSignatureInfo';
import { getPublicPortalURL } from 'CorePackage/MainModule/reducers/coreReducer';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';

import styles from './DecisionInfo.module.scss';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { TitleSectionElements } from './TitleSectionElements';
import { DecisionReason } from './DecisionReason';
import { MassDeleteInfo } from '../../../KnmPackage/MainModule/components/MassDeleteInfo';
import { IKnmMassDelete } from '../../../APIPackage/KnmModule/interfaces/IKnmMassDelete';
import { MassCancelInfo } from '../../../KnmPackage/MainModule/components/MassCancelInfo';
import { IKnmMassCancel } from '../../../APIPackage/KnmModule/interfaces/IKnmMassCancel';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IDecisionInfoProps {
  className?: string;
  watchOnly: boolean;
  statusCode?: IKnmStatus;
  kindControlFormValue?: IDictionarySupervisionTypeSelectOption;
  created: string | undefined;
  reasons?: IDecisionFormReason[] | undefined;
}

interface IDecisionInfoStoreProps {
  formRef: IDecisionFormRef | undefined;
  isNewDecision: boolean;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  decisionKindsSelectOptions: IDictionaryDecisionKindSelectOption[] | undefined;
  isSupervisor: boolean;
  accessToken: string | null | undefined;
  signed: boolean | null | undefined;
  signatureInfo: IKnmSignatureInfo[] | null | undefined;
  publicPortalURL: string;
  decisionId: string | undefined;
  settings: IAdminSetting[] | undefined;
  massCancelData: IKnmMassCancel | null | undefined;
  massDeleteData: IKnmMassDelete | null | undefined;
}

const defaultReason: IDecisionFormReason = {
  reason: undefined,
  type: undefined,
  text: undefined,
  date: undefined,
  riskIndicators: undefined,
  requirementDetails: undefined,
  dateRequirement: undefined,
  assignmentNumber: undefined,
  assignmentDate: undefined,
  numGuid: undefined,
  dateCreate: undefined,
  dateBegin: undefined,
  dateEnd: undefined,
  order: undefined,
  ervkUuid: undefined,
  links: undefined,
  listId: uuidv4(),
  fullName: undefined,
  indicatorRiskAttachment: undefined,
  indicatorRiskAuto: undefined,
  lastConductedKnmDate: undefined,
  prescriptionDate: undefined,
  prescriptionNumber: undefined,
  programAttachment: undefined,
  programNumber: undefined,
  prosecutorAttachment: undefined,
};

const DecisionInfoWithoutStore = ({
  className = '',
  watchOnly,
  statusCode,
  formRef,
  isNewDecision,
  knoOrganizationsSelectOptions,
  supervisionTypesSelectOptions,
  decisionKindsSelectOptions,
  isSupervisor,
  kindControlFormValue,
  accessToken,
  signed,
  signatureInfo,
  publicPortalURL,
  decisionId,
  settings,
  created,
  massCancelData,
  massDeleteData,
  reasons,
}: IDecisionInfoProps & IDecisionInfoStoreProps) => {
  const isDisableInfo = !isSupervisor || !isNewDecision;
  const qrRef = useRef<HTMLDivElement>(null);

  const isDecisionReasonValidate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_53_STARTS_FROM,
  );

  const handleKnoOrganizationChange = useCallback(() => {
    formRef?.current.change('kindControl');
    formRef?.current.change('kind');
    formRef?.current.change('organization');
    formRef?.current.change('objects');
    formRef?.current.change('reasons', [defaultReason]);
  }, [formRef]);

  useEffect(() => {
    if (!reasons || reasons.length === 0) {
      formRef?.current.change('reasons', [defaultReason]);
    }
  }, [isDecisionReasonValidate]);

  const handleKindControlChange = useCallback(
    (value: IDictionarySupervisionTypeSelectOption) => {
      // TODO добавить сброс полей после выбора вида контроля
      formRef?.current.change(
        'kindControl',
        !value
          ? value
          : {
              ...value,
              label: value.selectedLabel || value.label,
            },
      );
      formRef?.current.change('kind');
      formRef?.current.change('organization');
      formRef?.current.change('objects');
      if (isDecisionReasonValidate) formRef?.current.change('reasons', [defaultReason]);
    },
    [formRef, isDecisionReasonValidate],
  );

  const handleKindChange = useCallback(() => {
    formRef?.current.change('organization');
    formRef?.current.change('objects');
    if (isDecisionReasonValidate) formRef?.current.change('reasons', [defaultReason]);
  }, [formRef, isDecisionReasonValidate]);

  const handleDownloadButton = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const canvas = qrRef.current?.lastChild as HTMLCanvasElement;
      const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');

      saveFile(pngUrl, `${decisionId ?? 'qr'}.png`);
    },
    [decisionId],
  );

  return (
    <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={DECISION_INFO}>
      <div className={styles.TitleContainer}>
        <h2 className="shared-title__with-margin">Общая информация</h2>
        {accessToken && (
          <TitleSectionElements
            isSigned={signed}
            signatureInfo={signatureInfo}
            publicPortalURL={publicPortalURL}
            qrRef={qrRef}
            accessToken={accessToken}
            handleDownloadButton={handleDownloadButton}
          />
        )}
      </div>

      <div className={styles.KnoOrganizationFieldBlock} id={KNO_ORGANIZATION_BLOCK_ID}>
        <div className="shared-group-margin">
          <label className="shared-group-label" htmlFor="knoOrganizationPm">
            {getTitleFieldWithRedStar({
              label: 'Контрольный (надзорный) орган',
              field: `knoOrganization`,
              isDecision: true,
            })}
          </label>
        </div>
        <SelectInputField
          id="knoOrganization"
          name="knoOrganization"
          options={knoOrganizationsSelectOptions}
          isClearable
          isSearchable
          disabled={Boolean(statusCode) || watchOnly || !isNewDecision || isDisableInfo}
          size="medium"
          onChange={handleKnoOrganizationChange}
        />
      </div>

      <div className={styles.KindControlFieldBlock} id={KIND_CONTROL_BLOCK_ID}>
        <div className="shared-group-margin">
          <label className="shared-group-label" htmlFor="supervisionTypePm">
            {getTitleFieldWithRedStar({
              label: 'Вид контроля (надзора) и его номер',
              field: `kindControl`,
              isDecision: true,
            })}
          </label>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующем поле:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </div>
        <SelectInputField
          id="kindControl"
          name="kindControl"
          options={supervisionTypesSelectOptions}
          isSearchable
          disabled={watchOnly || !isNewDecision || isDisableInfo}
          size="medium"
          onChange={handleKindControlChange}
        />
      </div>
      {kindControlFormValue !== null && (
        <>
          <div className={styles.KindFieldBlock} id={KIND_BLOCK_ID}>
            <div className="shared-group-margin">
              <label className="shared-group-label" htmlFor="pmType">
                {getTitleFieldWithRedStar({
                  label: 'Вид контрольного (надзорного) мероприятия, специального режима',
                  field: `kind`,
                  isDecision: true,
                })}
              </label>
              <Tooltip
                placement="right"
                type="info"
                content={
                  <div className="shared-tooltip-info-content">
                    Поле зависит от значения в следующем поле:
                    <ul>
                      <p>Контрольный (надзорный) орган</p>
                      <p>Вид контроля (надзора)</p>
                    </ul>
                  </div>
                }
              >
                <span className="shared-tooltip-info-icon" />
              </Tooltip>
            </div>
            <SelectInputField
              id="kind"
              name="kind"
              options={decisionKindsSelectOptions}
              disabled={Boolean(statusCode) || watchOnly || isDisableInfo}
              size="medium"
              onChange={handleKindChange}
            />
          </div>

          {!isDecisionReasonValidate && (
            <>
              <div className={cn(['shared-group-label', styles.BasisDocumentTitle])}>
                Дата и номер документа, на основании которого проводится мероприятие
              </div>

              <div
                className={cn(['shared-field-row', styles.BasisDocumentStartDateBlock])}
                id={BASIS_DOCUMENT_DATE_TIME_BLOCK_ID}
              >
                <div className="shared-field-row-block_width">
                  <label className="shared-field-label_row" htmlFor="startDate">
                    Дата документа
                  </label>
                </div>
                <DatePickerField
                  className="shared-row-field"
                  id="basisDocumentDateTime"
                  name="basisDocumentDateTime"
                  disabled={watchOnly || isDisableInfo}
                  timeFormat
                />
              </div>

              <div
                className={cn(['shared-field-row', styles.BasisDocumentNumberBlock])}
                id={BASIS_DOCUMENT_NUMBER_BLOCK_ID}
              >
                <div className="shared-field-row-block_width">
                  <label className="shared-field-label_row" htmlFor="stopDate">
                    Номер документа
                  </label>
                </div>
                <Row align="center">
                  <TextInputField name="basisDocumentNumber" disabled={isDisableInfo} />
                </Row>
              </div>
            </>
          )}

          {isDecisionReasonValidate && <DecisionReason created={created} />}
        </>
      )}
      <MassCancelInfo massCancel={massCancelData} />
      <MassDeleteInfo massDelete={massDeleteData} />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionInfoStoreProps => ({
  formRef: getDecisionFormRef(state),
  isNewDecision: getDecisionIsNew(state) || false,
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  supervisionTypesSelectOptions: getDecisionSupervisionTypesSelectOptions(state),
  decisionKindsSelectOptions: getDecisionDecisionKindsSelectOptions(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  accessToken: getDecisionDataAccessToken(state),
  signed: getDecisionDataSigned(state),
  signatureInfo: getDecisionDataSignatureInfo(state),
  publicPortalURL: getPublicPortalURL(state),
  decisionId: getDecisionDataDecisionId(state),
  settings: getAdminFetchSettings(state),
  massCancelData: getDecisionMassCancel(state),
  massDeleteData: getDecisionMassDelete(state),
});

export const DecisionInfo = connect(mapStateToProps)(DecisionInfoWithoutStore);
